import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { formatApiError, authFetch } from '../utils/api';
import { Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const { language } = useLanguage();

  const t = language === 'el' ? {
    title: 'Νέος Κωδικός',
    password: 'Νέος κωδικός (τουλάχιστον 6 χαρακτήρες)',
    submit: 'Αλλαγή Κωδικού',
    submitting: 'Αλλαγή...',
    success: 'Ο κωδικός άλλαξε επιτυχώς!',
    login: 'Σύνδεση',
    invalidToken: 'Μη έγκυρος σύνδεσμος επαναφοράς.'
  } : {
    title: 'Reset Password',
    password: 'New password (at least 6 characters)',
    submit: 'Reset Password',
    submitting: 'Resetting...',
    success: 'Password reset successful!',
    login: 'Log In',
    invalidToken: 'Invalid reset link.'
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050811] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-red-400 mb-4">{t.invalidToken}</p>
          <Link to="/login" className="text-cyan-400 hover:text-cyan-300">{t.login}</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('submitting');
    try {
      const res = await authFetch('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw data;
      }
      setStatus('success');
    } catch (err) {
      setError(formatApiError(err.detail));
      setStatus('idle');
    }
  };

  return (
    <div className="min-h-screen bg-[#050811] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8">
          <h1 data-testid="reset-title" className="text-3xl font-bold text-white mb-8 text-center">{t.title}</h1>

          {status === 'success' ? (
            <div className="text-center">
              <p data-testid="reset-success" className="text-emerald-400 mb-4">{t.success}</p>
              <Link to="/login" data-testid="reset-login-link" className="text-cyan-400 hover:text-cyan-300">{t.login}</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="relative">
                <Lock size={18} className="absolute left-3.5 top-3.5 text-white/40" />
                <input data-testid="reset-password" type={showPassword ? 'text' : 'password'} required minLength={6} placeholder={t.password} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg pl-11 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/30 transition-colors" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-3.5 text-white/40 hover:text-white/70">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {error && <p data-testid="reset-error" className="text-red-400 text-sm text-center">{error}</p>}
              <Button data-testid="reset-submit" type="submit" disabled={status === 'submitting'} className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white" size="lg">
                {status === 'submitting' ? t.submitting : t.submit}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
